import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import FiberManualRecordRoundedIcon from "@mui/icons-material/FiberManualRecordRounded";
import FeatureCard from "./FeatureCard";
import LottieShowcase from "./LottieShowcase";
import {
  agendaHighlights,
  features,
  growthHighlights,
  lottieSources,
} from "./landingContent";

const baseGlassCardStyles = (isDarkMode: boolean) => ({
  position: "relative",
  overflow: "hidden",
  borderRadius: { xs: 4, md: 6 },
  border: isDarkMode
    ? "1px solid rgba(94, 145, 193, 0.34)"
    : "1px solid rgba(104, 170, 224, 0.24)",
  p: { xs: 3, md: 5 },
  backdropFilter: "blur(10px)",
});

export default function Features() {
  return (
    <Container id="features" maxWidth="lg" sx={{ pb: { xs: 8, md: 10 } }}>
      <Stack spacing={{ xs: 7, md: 8 }}>
        <Stack spacing={2}>
          <Stack
            direction="row"
            spacing={1.2}
            alignItems="center"
            sx={{ color: "primary.main" }}
          >
            <AutoAwesomeRoundedIcon sx={{ fontSize: 18 }} />
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: 13,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
              }}
            >
              Ecossistema esportivo premium
            </Typography>
          </Stack>
          <Typography
            variant="h2"
            sx={{ fontSize: { xs: "1.7rem", md: "2.3rem" }, maxWidth: 900 }}
          >
            Cada modulo foi desenhado para manter ritmo operacional,
            previsibilidade financeira e experiencia moderna para seus clientes
          </Typography>
        </Stack>

        <Grid container spacing={2}>
          {features.map((item) => (
            <Grid item xs={12} md={6} lg={4} key={item.title}>
              <FeatureCard {...item} />
            </Grid>
          ))}
        </Grid>

        <Box
          id="agenda-ritmo"
          sx={(theme) => ({
            ...baseGlassCardStyles(theme.palette.mode === "dark"),
            bgcolor:
              theme.palette.mode === "dark"
                ? "rgba(13, 26, 39, 0.78)"
                : "rgba(236, 247, 255, 0.68)",
          })}
        >
          <Box
            sx={(theme) => ({
              position: "absolute",
              inset: 0,
              background:
                theme.palette.mode === "dark"
                  ? "radial-gradient(circle at 14% 24%, rgba(61,131,179,0.23), rgba(13,26,39,0) 46%), radial-gradient(circle at 90% 88%, rgba(74,146,207,0.18), rgba(13,26,39,0) 42%)"
                  : "radial-gradient(circle at 14% 24%, rgba(113,196,255,0.2), rgba(236,247,255,0) 46%), radial-gradient(circle at 90% 88%, rgba(133,205,255,0.16), rgba(236,247,255,0) 42%)",
            })}
          />
          <Grid
            container
            spacing={{ xs: 4, md: 6 }}
            alignItems="center"
            sx={{ position: "relative" }}
          >
            <Grid item xs={12} lg={6}>
              <LottieShowcase src={lottieSources.agenda} />
            </Grid>
            <Grid item xs={12} lg={6}>
              <Stack spacing={2.5}>
                <Chip
                  label="Agenda em ritmo de jogo"
                  color="primary"
                  sx={{ width: "fit-content" }}
                />
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { xs: "1.6rem", md: "2rem" },
                    lineHeight: 1.2,
                  }}
                >
                  Horarios, encaixes e ocupacao com clareza operacional para
                  toda a equipe
                </Typography>
                <Typography
                  color="text.secondary"
                  sx={{ fontSize: { xs: 16, md: 18 } }}
                >
                  Da recepcao ao financeiro, a agenda passa a funcionar com
                  leitura imediata e decisoes mais rapidas em cada turno.
                </Typography>
                <Stack spacing={1.5}>
                  {agendaHighlights.map((item) => (
                    <Stack
                      key={item}
                      direction="row"
                      spacing={1.2}
                      alignItems="flex-start"
                    >
                      <FiberManualRecordRoundedIcon
                        sx={{ color: "primary.main", fontSize: 12, mt: 0.9 }}
                      />
                      <Typography sx={{ color: "text.primary" }}>
                        {item}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Stack>
            </Grid>
          </Grid>
        </Box>

        <Box
          id="crescimento"
          sx={(theme) => ({
            ...baseGlassCardStyles(theme.palette.mode === "dark"),
            bgcolor:
              theme.palette.mode === "dark"
                ? "rgba(11, 24, 36, 0.76)"
                : "rgba(238, 249, 255, 0.64)",
          })}
        >
          <Box
            sx={(theme) => ({
              position: "absolute",
              inset: 0,
              background:
                theme.palette.mode === "dark"
                  ? "radial-gradient(circle at 82% 24%, rgba(61,130,178,0.24), rgba(11,24,36,0) 48%), radial-gradient(circle at 12% 86%, rgba(74,144,205,0.2), rgba(11,24,36,0) 46%)"
                  : "radial-gradient(circle at 82% 24%, rgba(111,200,255,0.22), rgba(236,247,255,0) 48%), radial-gradient(circle at 12% 86%, rgba(131,204,255,0.16), rgba(236,247,255,0) 46%)",
            })}
          />
          <Grid
            container
            spacing={{ xs: 4, md: 6 }}
            alignItems="center"
            sx={{ position: "relative" }}
          >
            <Grid item xs={12} lg={6}>
              <Stack spacing={2.5}>
                <Chip
                  label="Crescimento e recorrencia"
                  color="primary"
                  sx={{ width: "fit-content" }}
                />
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { xs: "1.6rem", md: "2rem" },
                    lineHeight: 1.2,
                  }}
                >
                  Fidelizacao esportiva continua para crescimento previsivel de
                  receita
                </Typography>
                <Typography
                  color="text.secondary"
                  sx={{ fontSize: { xs: 16, md: 18 } }}
                >
                  Consolide mensalistas, acompanhe performance comercial e
                  mantenha evolucao sustentavel com dados claros para cada
                  decisao.
                </Typography>
                <Stack spacing={1.5}>
                  {growthHighlights.map((item) => (
                    <Stack
                      key={item}
                      direction="row"
                      spacing={1.2}
                      alignItems="flex-start"
                    >
                      <FiberManualRecordRoundedIcon
                        sx={{ color: "primary.main", fontSize: 12, mt: 0.9 }}
                      />
                      <Typography sx={{ color: "text.primary" }}>
                        {item}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Stack>
            </Grid>
            <Grid item xs={12} lg={6}>
              <LottieShowcase src={lottieSources.growth} flipX />
            </Grid>
          </Grid>
        </Box>

        <Box
          sx={(theme) => ({
            borderRadius: { xs: 4, md: 6 },
            border: "1px solid",
            borderColor:
              theme.palette.mode === "dark"
                ? "rgba(90, 143, 189, 0.34)"
                : "primary.light",
            background:
              theme.palette.mode === "dark"
                ? "linear-gradient(90deg, rgba(12,25,37,0.95) 0%, rgba(12,30,46,0.92) 45%, rgba(14,33,50,0.95) 100%)"
                : "linear-gradient(90deg, rgba(232,244,255,0.95) 0%, rgba(242,251,255,0.9) 45%, rgba(255,255,255,0.95) 100%)",
            p: { xs: 3, md: 5 },
          })}
        >
          <Stack spacing={3}>
            <Typography
              variant="h3"
              sx={{ fontSize: { xs: "1.5rem", md: "1.95rem" }, maxWidth: 850 }}
            >
              Transforme sua operacao esportiva em um sistema previsivel,
              escalavel e pronto para crescer com consistencia
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Box
                  sx={(theme) => ({
                    p: 2.5,
                    height: "100%",
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor:
                      theme.palette.mode === "dark"
                        ? "rgba(87, 136, 178, 0.38)"
                        : "primary.light",
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(16, 31, 45, 0.82)"
                        : "rgba(255,255,255,0.88)",
                  })}
                >
                  <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
                    Implantacao guiada
                  </Typography>
                  <Typography color="text.secondary">
                    Empresa, quadras, planos e regras operacionais configurados
                    com rapidez e padrao profissional.
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box
                  sx={(theme) => ({
                    p: 2.5,
                    height: "100%",
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor:
                      theme.palette.mode === "dark"
                        ? "rgba(87, 136, 178, 0.38)"
                        : "primary.light",
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(16, 31, 45, 0.82)"
                        : "rgba(255,255,255,0.88)",
                  })}
                >
                  <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
                    Execucao de alta performance
                  </Typography>
                  <Typography color="text.secondary">
                    Agenda, recorrencia e financeiro integrados em uma visao
                    unica para decisoes imediatas.
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box
                  sx={(theme) => ({
                    p: 2.5,
                    height: "100%",
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor:
                      theme.palette.mode === "dark"
                        ? "rgba(87, 136, 178, 0.38)"
                        : "primary.light",
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(16, 31, 45, 0.82)"
                        : "rgba(255,255,255,0.88)",
                  })}
                >
                  <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
                    Escala com previsibilidade
                  </Typography>
                  <Typography color="text.secondary">
                    Aumente retencao, receita e confianca do cliente com um
                    produto SaaS esportivo premium.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Stack>
        </Box>
      </Stack>
    </Container>
  );
}
